import React, { useCallback, useEffect, useRef, useState } from 'react'

import { ChevronRight, CloudPlus, FolderFill, X, XCircle } from 'react-bootstrap-icons'
import { useCombobox, UseComboboxState, UseComboboxStateChangeOptions } from 'downshift'

import { SelectLocalArtifactsProps } from './SelectLocalArtifacts.types'

import { getHost } from '../hooks'
import { ComboBox } from '../atoms'

import { getFileIcon } from './getFileTypeIcon'

interface SystemHandle { 
  kind: 'file' | 'directory', 
  name: 'string' 
}

export const SelectLocalArtifacts: React.FC<SelectLocalArtifactsProps> = ({
  onChange,
}) => {
  
  const host = getHost()

  const [loading, setLoading] = useState(false)
  const [artifacts, setArtifacts] = useState<{folders: SystemHandle[], files: SystemHandle[]}>({ folders: [], files: [] })
  const directoryRef = useRef([])

  const [path, setPath] = useState<string[]>([])
  const [selArtifact, setSelArtifact] = useState<SystemHandle>()

  const listLocalArtifacts = useCallback(async (directoryHandle: any) => {
    if (!directoryHandle) return

    const artifactHandles = []

    for await (const artifactHandle of directoryHandle.values()) {

      artifactHandles.push(artifactHandle)
    }
    
    return artifactHandles
    
  }, [])
  
  useEffect(() => {
    if (host !== 'web' && directoryRef.current.length < 1) {
      return
    }

    setLoading(true)

    listLocalArtifacts(directoryRef.current[directoryRef.current.length - 1])
      .then((facts) => {
        
        const folders = facts.filter(f => f.kind === 'directory')
        const files = facts.filter(f => f.kind === 'file')
        
        setArtifacts({ folders, files })

      }) 
      .finally(() => setLoading(false))

  }, [host, listLocalArtifacts, path])

  useEffect(() => {
    if (!selArtifact) return

    if (selArtifact.kind === 'file') {

      const getFile = async(fileHandle) => {
        const path = await directoryRef.current[0].resolve(selArtifact)
        path.unshift(directoryRef.current[0].name)
        onChange && onChange(await fileHandle.getFile(), path)
      } 
      
      getFile(selArtifact)
    
    } else {

      directoryRef.current[directoryRef.current.length - 1]
        // @ts-ignore
        .getDirectoryHandle(selArtifact.name)
        .then(ref => {
          directoryRef.current.push(ref)
        })
        .finally(() => {
          setPath(path => [`${path}/${selArtifact.name}`])
        })

    }
  }, [onChange, selArtifact])

  const stateReducer = useCallback((state: UseComboboxState<{ id: string; name: string; }>, actionAndChanges: UseComboboxStateChangeOptions<{ id: string; name: string; }>) => {
    const { type, changes } = actionAndChanges

    switch (type) {
      case useCombobox.stateChangeTypes.FunctionCloseMenu:
      case useCombobox.stateChangeTypes.InputBlur :
        // @ts-ignore
        if (changes.selectedItem && changes.selectedItem.kind === 'directory') {
          return { ...changes, isOpen: true }
        } else return changes
      default:
        return changes
    }
  }, [])

  return (
    <ComboBox
      // @ts-ignore
      items={[...artifacts.folders, ...artifacts.files].filter(a => !a.name.startsWith('.')).map(a => {
        // @ts-ignore
        a.id = a.name
        return a
      }).sort((a, b) => a.kind === 'directory' ? -1 : 1)}
      renderItem={(item) => {
              
        const tokens = item.name.split('.')
        const extension = tokens[tokens.length - 1]

        const icon = item.kind === 'file' ? getFileIcon(extension) : <FolderFill/>

        return (
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center'
            }}
          >
            {icon}{item.name}
          </div>
        )
      }}
      inputProps={{
        onClick: async (e) => {
          if(directoryRef.current.length < 1) {
            e.preventDefault()
            e.stopPropagation()

            window
              .showDirectoryPicker({
                startsIn: 'desktop',
                recursive: false
              })
              .then(ref => {
                directoryRef.current.push(ref)
                setPath(path => [...path, `/${ref.name}`])
              })
            
          }
        }
      }}
      setSelected={setSelArtifact}
      loading={loading}
      icon={<CloudPlus />}
      header={
        path.length > 0 &&
        <div
          key="header"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            color: 'var(--slate11)',
            padding: '6px 0px',
            gap: 4,
            alignItems: 'center',
            paddingLeft: 4,
            // zIndex: 1000,
          }}
        >
          <button
            key={'root'}
            style={{
              display: 'flex',
              border:'none',
              backgroundColor: 'none',
              borderRadius: 999,
              padding: '2px 8px',
              cursor: 'pointer',
              alignItems: 'center',
              gap: 8,
            }}
            onClick={(e) => {
              setPath([])
              directoryRef.current = []
              if (host !== 'web') {
                setArtifacts({ folders: [], files: [] })
              }
            }}
          >
            {host === 'web' ? <FolderFill/> : <XCircle/>}
            {host === 'web' ? 'Root' : 'Reset'}
          </button>
          <ChevronRight/>
          {path[0].split('/').slice(1).map((s, i, array) => (
            <>
              <button
                key={s}
                style={{
                  border:'none',
                  backgroundColor: 'none',
                  borderRadius: 999,
                  padding: '2px 8px',
                  cursor: 'pointer',
                  maxWidth: 100,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
                onClick={(e) => {
              
                  const newPath = '/' + array.slice(0,i + 1).join('/')
                  const newPathArray = newPath === '' ? [] : [newPath]
                  setPath(newPathArray)

                  if (i > directoryRef.current.length - 1) {
                    directoryRef.current[directoryRef.current.length - 1]
                      .getDirectoryHandle()
                      .then(ref => {
                        directoryRef.current.push(ref)
                      })
                  } else {
                    directoryRef.current = directoryRef.current.slice(0, i + 1)
                  }
                }}
              >
                {s}
              </button>
              {i !== array.length -1 && <ChevronRight/>}
            </>
          ))}
        </div>
      }
      footer={(
        <div
          key="footer"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            color: 'var(--slate11)',
            padding: '6px 0px',
          }}
        >
          {`Showing ${artifacts.folders.length ?? 0} folder${artifacts.folders.length === 1 ? '' : 's'}, and ${artifacts.files.length ?? 0} file${artifacts.files.length === 1 ? '' : 's'}.`}
        </div>
      )}
      maxHeight={340}
      stateReducer={stateReducer}
    />
  )
}

export default SelectLocalArtifacts 